import React, { useContext, useState } from 'react'
import { __ } from 'i18n-for-browser'
import '../../config/i18n'
import { SkycityContext } from '../../lib/SkycityProvider'

import { error } from '@react/react-spectrum/Toast'
import Button from '@react/react-spectrum/Button'

import { ssApiSyncCall } from '../../lib/ccApi.js'
import {
    getFilename,
    getFilenameWithoutExt,
    getEncodedURI,
    getTempDirectory,
    getSenseiHeaders,
    checkFileType
} from '../../lib/skycity.js'

import SenseiParams from './SenseiParams'

import axios from 'axios'
import {
    exploreIndexDocument,
    exploreHomeDirectory,
    uploadFile,
    retrieveRendition,
    retrievePreSignedDownloadUrl
} from '../../lib/acpApi.js'
const LinkHeader = require('http-link-header')
const uuid = require('uuid/v4')

export default function Operation () {
    const {
        errorOptions,
        dispatch,
        inputFiles,
        inputSecondFiles,
        inputThirdFiles
    } = useContext(SkycityContext)

    const [params, setParams] = useState({
        version: [
            { label: 'v2', value: 'v2', selected: true },
            { label: 'v3', value: 'v3', selected: false },
            { label: 'v4', value: 'v4', selected: false },
            { label: 'v5', value: 'v5', selected: false },
            { label: 'v6', value: 'v6', selected: false }
        ]
    })

    async function start (image, background, objectMasks) {
        const files = []
        //  replace with presignedUrl
        const imgFilePath = image.path
        const backgroundFilePath = background.path
        const masks = []
        for (let i = 0; i < objectMasks.length; i++) {
            const maskPath = objectMasks[i].path
            masks.push({
                'dc:format': checkFileType(objectMasks[i].type),
                'sensei:repoType': 'HTTP',
                'repo:path': maskPath
            })
        }

        const codePayload = {
            contentAnalyzerRequests: {
                'sensei:name': 'Layer Scene Completion',
                'sensei:invocation_mode': 'synchronous',
                'sensei:invocation_batch': false,
                'sensei:in_response': false,
                'sensei:engines': [
                    {
                        'sensei:execution_info': {
                            'sensei:engine': 'Classification:autocrop:Service-6f8d9639dfa74b1897498cd96a9c6527'
                        },
                        'sensei:params': {
                            version: params.version.find(v => v.selected).value
                        },
                        'sensei:inputs': {
                            image: {
                                'dc:format': checkFileType(image.type),
                                'sensei:repoType': 'HTTP',
                                'repo:path': imgFilePath
                            },
                            background: {
                                'dc:format': checkFileType(background.type),
                                'sensei:repoType': 'HTTP',
                                'repo:path': backgroundFilePath
                            },
                            object_masks: masks
                        },
                        'sensei:outputs': {
                            completed_objects: {
                                'dc:format': 'image/png',
                                'sensei:multipart_field_name': 'completed_objects'
                            },
                            detected_objects: {
                                'dc:format': 'application/json',
                                'sensei:multipart_field_name': 'detected_objects'
                            },
                            original_index_map: {
                                'dc:format': 'application/json',
                                'sensei:multipart_field_name': 'original_index_map'
                            },
                            occlusion_relationships: {
                                'dc:format': 'application/json',
                                'sensei:multipart_field_name': 'occlusion_relationships'
                            },
                            metadata: {
                                'dc:format': 'application/json',
                                'sensei:multipart_field_name': 'metadata'
                            }
                        }
                    }
                ]
            }
        }

        const formData = new FormData()
        formData.append('contentAnalyzerRequests', JSON.stringify(codePayload.contentAnalyzerRequests))
        const config = {
            file: image,
            url: `${process.env.REACT_APP_CONFIG_ENDPOINT_MLSENSEI}/predict`,
            method: 'POST',
            mode: 'cors',
            timeout: 90000,
            headers: getSenseiHeaders(),
            payload: codePayload,
            body: {
                formData: formData
            }
        }

        /* *********************************** */
        dispatch({ type: 'set-codes', payload: { config: config } })

        const responseBody = await ssApiSyncCall(config)
        const contentAnalyzerResponse = responseBody.get('contentAnalyzerResponse')
        const response = JSON.parse(responseBody.get('metadata'))
        response.sensei_metadata = JSON.parse(contentAnalyzerResponse)
        response.detected_objects = JSON.parse(responseBody.get('detected_objects'))
        response.original_index_map = JSON.parse(responseBody.get('original_index_map'))
        response.occlusion_relationships = JSON.parse(responseBody.get('occlusion_relationships'))
        const responseImages = response.sensei_metadata.statuses[0].invocations[0]['sensei:outputs'].completed_objects
        // 1. Explore the Index Document and get the Primary URL of the home Directory
        const homeDirectoryPrimaryUrl = await exploreIndexDocument()

        // 2. Explore the home Directory and get the Create URL of the Cloud Documents Directory
        const exploreHomeDirectoryResponse = await exploreHomeDirectory(
            homeDirectoryPrimaryUrl
        )
        const cloudDocumentsCreateUrl = exploreHomeDirectoryResponse.createLink

        for (let i = 0; i < responseImages.length; ++i) {
            const output = responseBody.get(responseImages[i]['sensei:multipart_field_name'])
            const blob = output.slice(0, output.size, 'image/png')

            const outputFileName = `layerscene_${uuid()}_${image.shortName}.png`
            const newFile = new File([blob], outputFileName, { type: 'image/png' })
            const outputHref = `${getTempDirectory()}/${outputFileName}`
            const fileUploaded = await uploadFile(
                cloudDocumentsCreateUrl,
                newFile
            )
            const uploadlinks = LinkHeader.parse(fileUploaded.headers.get('link'))
            const primaryUrl = uploadlinks.refs.filter(val => val.rel.match('http://ns.adobe.com/adobecloud/rel/primary'))[0].uri
            const renditionUrl = uploadlinks.refs.filter(val => val.rel.match('http://ns.adobe.com/adobecloud/rel/rendition'))[0].uri
            const preSignedDownloadUrl = await retrievePreSignedDownloadUrl(primaryUrl)
            const largeRendition = await retrieveRendition(
                renditionUrl,
                0,
                'image/jpeg'
            )
            files.push({
                inputFile: image,
                name: getFilename(outputHref),
                shortName: getFilenameWithoutExt(outputHref),
                path: outputHref,
                outputThumbnail: largeRendition,
                output: largeRendition,
                outputToDownload: preSignedDownloadUrl
            })
        }

        dispatch({
            type: 'skycity-success',
            payload: {
                files: files,
                response: response,
                loading: false,
                wait: false
            }
        })
    }

    async function run (e) {
        try {
            dispatch({ type: 'skycity-run', payload: {} })

            const funcArray = []
            inputFiles.forEach(inputFile => {
                inputSecondFiles.forEach(secondFile => {
                    funcArray.push(start(inputFile, secondFile, inputThirdFiles))
                })
            })

            await axios.all(funcArray)
        } catch (e) {
            console.log(e)
            error(`${__('error_msg_title')}: ${e}`, {
                timeout: 0
            })
            errorOccured(`${__('error_msg_title')}: ${e}`)
        } finally {
            console.log('finally...')
            dispatch({
                type: 'sensei-end-wait'
            })
        }
    }

    function isRunDisabled () {
        if (inputFiles && inputFiles.length > 0) {
            return false
        }
        return true
    }

    function errorOccured (errMsg, res) {
        error(errMsg.slice(0, errorOptions.wordSlice), {
            onClose: () => console.log('close'),
            timeout: errorOptions.msgTimeout
        })
        dispatch({ type: 'error-occured', payload: { errMsg: errMsg, response: res, loading: false, wait: false } })
    }

    try {
        return <div>
            <SenseiParams params={params} setParams={setParams} />
            <div className="adobe-skycity-button-right">
                <Button variant="cta" onClick={async e => await run(e)} disabled={isRunDisabled()}>{__('btn_run')}</Button>
            </div>
        </div>
    } catch (e) {
        error(`${__('error_msg_title')}: ${e}`, {
            timeout: 0
        })
    }
}
